import { ChevronDown, Eye, MoreHorizontal } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCan } from '@/auth/AuthContext';
import { Button, DropdownMenu, type DropdownItem } from '@/components/ui';
import { cn } from '@/lib/cn';
import { ACTION_META, ACTION_ORDER } from './actionMeta';
import { usePharmacyActions } from './PharmacyActionsContext';
import type { PharmacyRef } from './types';

interface PharmacyActionsMenuProps {
  pharmacy: PharmacyRef;
  /** `icon` = three-dot row trigger; `button` = labelled button for page headers. */
  variant?: 'icon' | 'button';
  includeViewDetails?: boolean;
}

export function PharmacyActionsMenu({ pharmacy, variant = 'icon', includeViewDetails = true }: PharmacyActionsMenuProps) {
  const navigate = useNavigate();
  const { openAction } = usePharmacyActions();
  const can = useCan();

  const items: DropdownItem[] = [
    ...(includeViewDetails
      ? [{ id: 'view', label: 'View Details', icon: <Eye size={16} />, onSelect: () => navigate(`/pharmacies/${pharmacy.id}`) }]
      : []),
    // Actions outside the user's role are hidden, not disabled.
    ...ACTION_ORDER.filter((action) => can(ACTION_META[action].permission)).map((action, i) => {
      const meta = ACTION_META[action];
      const Icon = meta.icon;
      return {
        id: action,
        label: meta.label,
        icon: <Icon size={16} />,
        separatorBefore: (includeViewDetails && i === 0) || action === 'edit',
        onSelect: () => openAction(action, pharmacy),
      };
    }),
  ];
  if (items.length === 0) return null;

  return (
    <DropdownMenu
      items={items}
      label={`Actions for ${pharmacy.name}`}
      renderTrigger={(props) =>
        variant === 'icon' ? (
          <button
            {...props}
            type="button"
            aria-label={`Actions for ${pharmacy.name}`}
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded-md text-ink-secondary transition-colors hover:bg-surface-sunken hover:text-ink focus-visible:shadow-focus focus-visible:outline-none',
              props['aria-expanded'] && 'bg-surface-sunken text-ink',
            )}
          >
            <MoreHorizontal size={18} aria-hidden="true" />
          </button>
        ) : (
          <Button {...props} variant="primary" rightIcon={<ChevronDown size={16} aria-hidden="true" />}>
            Actions
          </Button>
        )
      }
    />
  );
}
